import React, { useState, useEffect, useRef, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaGraduationCap, FaHeartbeat, FaShieldAlt, FaHandHoldingHeart, FaUsers, FaHandsHelping, FaChild, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube, FaChevronLeft, FaChevronRight, FaPhone, FaEnvelope, FaMapMarkerAlt, FaDonate, FaHeart, FaPlay, FaPause, FaQuoteLeft, FaStar, FaArrowRight, FaCalendarAlt, FaAward, FaCheckCircle, FaBullseye } from 'react-icons/fa';

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.1
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};



// Floating Donation Widget
const FloatingDonationWidget = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 3000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 100 }}
                    className="fixed right-4 top-1/2 transform -translate-y-1/2 z-50 bg-gradient-to-r from-red-500 to-pink-500 text-white p-4 rounded-full shadow-lg cursor-pointer"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsVisible(false)}
                >
                    <div className="flex items-center gap-2">
                        <FaDonate className="text-xl" />
                        <span className="hidden md:block font-bold">Donate Now</span>
                    </div>
                    <motion.div
                        className="absolute -top-1 -right-1 bg-yellow-400 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                    >
                        ❤️
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Live Updates Ticker
const LiveUpdatesTicker = () => {
    const updates = [
        "🎉 50 children enrolled in school this month!",
        "💊 Free health camp organized in 3 villages",
        "📚 500+ books distributed to rural libraries",
        "👩‍🏫 15 women completed vocational training",
        "🏥 100+ children received medical checkups"
    ];

    const [currentUpdate, setCurrentUpdate] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentUpdate((prev) => (prev + 1) % updates.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [updates.length]);

    return (
        <motion.div
            className="bg-gradient-to-r from-green-500 to-blue-500 text-white py-2 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
        >
            <div className="max-w-7xl mx-auto px-4">
                <motion.div
                    key={currentUpdate}
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -100, opacity: 0 }}
                    className="text-center font-semibold"
                >
                    {updates[currentUpdate]}
                </motion.div>
            </div>
        </motion.div>
    );
};

// Success Story Card Component
const SuccessStoryCard = ({ story }) => {
    const [isFlipped, setIsFlipped] = useState(false);

    return (
        <motion.div
            className="relative h-80 cursor-pointer"
            onHoverStart={() => setIsFlipped(true)}
            onHoverEnd={() => setIsFlipped(false)}
            variants={scaleIn}
            whileHover={{ y: -10 }}
        >
            <motion.div
                className="absolute inset-0 bg-white rounded-lg shadow-lg overflow-hidden"
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.6 }}
                style={{ transformStyle: 'preserve-3d' }}
            >
                {/* Front */}
                <div className="absolute inset-0 bg-white rounded-lg p-6" style={{ backfaceVisibility: 'hidden' }}>
                    <img
                        src={story.beforeImage}
                        alt={story.title}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                    />
                    <h3 className="text-lg font-bold mb-2">{story.title}</h3>
                    <p className="text-gray-600 text-sm">{story.beforeText}</p>
                    <div className="absolute bottom-4 right-4 text-red-500">
                        <FaHeart />
                    </div>
                </div>

                {/* Back */}
                <div
                    className="absolute inset-0 bg-gradient-to-br from-green-400 to-blue-500 rounded-lg p-6 text-white"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                >
                    <img
                        src={story.afterImage}
                        alt={story.title}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                    />
                    <h3 className="text-lg font-bold mb-2">{story.title} - Today</h3>
                    <p className="text-sm">{story.afterText}</p>
                    <div className="absolute bottom-4 right-4 text-yellow-300">
                        <FaStar />
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

// Testimonial Carousel Component
const TestimonialCarousel = () => {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    const testimonials = [
        {
            name: "Priya Sharma",
            role: "Monthly Donor",
            image: "/images/testimonial1.jpg",
            text: "Seeing the direct impact of my monthly donation on children's education fills my heart with joy. These kids deserve every opportunity to succeed.",
            rating: 5,
            location: "Mumbai"
        },
        {
            name: "Rajesh Kumar",
            role: "Volunteer",
            image: "/images/testimonial2.jpg",
            text: "Volunteering with Ummeed Ki Kiran has been life-changing. The smiles on children's faces make every effort worthwhile.",
            rating: 5,
            location: "Delhi"
        },
        {
            name: "Anita Verma",
            role: "Corporate Sponsor",
            image: "/images/testimonial3.jpg",
            text: "Our company's partnership with this NGO has shown us the true meaning of corporate social responsibility. Amazing work!",
            rating: 5,
            location: "Bangalore"
        }
    ];

    useEffect(() => {
        if (!isAutoPlaying) return;
        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [isAutoPlaying, testimonials.length]);

    return (
        <div className="relative max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold">What People Say</h3>
                <button
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                >
                    {isAutoPlaying ? <FaPause /> : <FaPlay />}
                    {isAutoPlaying ? 'Pause' : 'Play'}
                </button>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={currentTestimonial}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-xl shadow-lg p-8 relative"
                >
                    <FaQuoteLeft className="text-4xl text-blue-200 absolute top-4 left-4" />

                    <div className="flex items-start gap-6 mt-8">
                        <img
                            src={testimonials[currentTestimonial].image}
                            alt={testimonials[currentTestimonial].name}
                            className="w-16 h-16 rounded-full object-cover"
                            onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${testimonials[currentTestimonial].name}&background=random`;
                            }}
                        />
                        <div className="flex-1">
                            <p className="text-gray-700 mb-4 italic text-lg">
                                "{testimonials[currentTestimonial].text}"
                            </p>
                            <div className="flex items-center gap-2 mb-2">
                                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                                    <FaStar key={i} className="text-yellow-400" />
                                ))}
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-800">{testimonials[currentTestimonial].name}</h4>
                                <p className="text-gray-600">{testimonials[currentTestimonial].role} • {testimonials[currentTestimonial].location}</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-3 h-3 rounded-full transition-all ${index === currentTestimonial ? 'bg-blue-600 w-8' : 'bg-gray-300'
                            }`}
                    />
                ))}
            </div>
        </div>
    );
};

// Interactive Statistics Component
const InteractiveStats = ({ targetValue, suffix = '', label, icon, color, shouldStart }) => {
    const [count, setCount] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (!shouldStart) return;

        let startTime;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / 2000, 1);
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.floor(easeOutQuart * targetValue);

            setCount(currentCount);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(targetValue);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [targetValue, shouldStart]);

    return (
        <motion.div
            className={`relative overflow-hidden bg-white rounded-lg shadow-md p-8 text-center cursor-pointer`}
            variants={scaleIn}
            whileHover={{
                scale: 1.05,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                y: -10
            }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            transition={{ duration: 0.3 }}
        >
            <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0`}
                animate={{ opacity: isHovered ? 0.1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            <motion.div
                className={`text-4xl mb-4 ${color.includes('blue') ? 'text-blue-600' : color.includes('green') ? 'text-green-600' : color.includes('purple') ? 'text-purple-600' : 'text-red-600'}`}
                animate={{
                    rotate: isHovered ? 360 : 0,
                    scale: isHovered ? 1.2 : 1
                }}
                transition={{ duration: 0.5 }}
            >
                {icon}
            </motion.div>

            <motion.span
                className="counter-number block text-5xl font-bold text-gray-800 mb-2"
                animate={{ scale: isHovered ? 1.1 : 1 }}
            >
                {count.toLocaleString()}{suffix}
            </motion.span>

            <span className="text-gray-600 font-medium">{label}</span>

            <motion.div
                className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${color}`}
                initial={{ width: 0 }}
                animate={{ width: isHovered ? '100%' : '0%' }}
                transition={{ duration: 0.3 }}
            />
        </motion.div>
    );
};

// Volunteer Opportunities Component
const VolunteerOpportunities = () => {
    const opportunities = [
        {
            title: "Teaching Assistant",
            description: "Help children with their studies and homework",
            timeCommitment: "2 hours/week",
            location: "Varanasi Centers",
            urgent: true,
            icon: <FaGraduationCap />
        },
        {
            title: "Health Camp Volunteer",
            description: "Assist in organizing medical camps",
            timeCommitment: "1 day/month",
            location: "Rural Villages",
            urgent: false,
            icon: <FaHeartbeat />
        },

    ];

    return (
        <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={staggerContainer}
        >
            {opportunities.map((opportunity, index) => (
                <motion.div
                    key={index}
                    className="bg-white rounded-lg shadow-md p-6 relative overflow-hidden"
                    variants={scaleIn}
                    whileHover={{ y: -5, boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.25)" }}
                >
                    {opportunity.urgent && (
                        <motion.div
                            className="absolute top-0 right-0 bg-red-500 text-white px-3 py-1 text-xs font-bold"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                        >
                            URGENT
                        </motion.div>
                    )}

                    <div className="text-3xl text-blue-600 mb-4">
                        {opportunity.icon}
                    </div>

                    <h3 className="text-xl font-bold mb-2">{opportunity.title}</h3>
                    <p className="text-gray-600 mb-4">{opportunity.description}</p>

                    <div className="space-y-2 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-2">
                            <FaCalendarAlt />
                            <span>{opportunity.timeCommitment}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <FaMapMarkerAlt />
                            <span>{opportunity.location}</span>
                        </div>
                    </div>

                    <motion.button
                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        Apply Now <FaArrowRight />
                    </motion.button>
                </motion.div>
            ))}
        </motion.div>
    );
};

// Custom hook for animated counter
const useAnimatedCounter = (targetValue, duration = 2000, shouldStart = false) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!shouldStart) return;

        let startTime;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);

            // Easing function for smooth animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.floor(easeOutQuart * targetValue);

            setCount(currentCount);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(targetValue);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [targetValue, duration, shouldStart]);

    return count;
};

// Custom hook for intersection observer
const useIntersectionObserver = (ref, options = {}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
            }
        }, {
            threshold: 0.3,
            ...options
        });

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);

    return isVisible;
};

// Animated Counter Component
const AnimatedCounter = ({ targetValue, suffix = '', label, shouldStart }) => {
    const count = useAnimatedCounter(targetValue, 2000, shouldStart);

    return (
        <motion.div
            className="counter-card bg-white rounded-lg shadow-md p-8 text-center"
            variants={scaleIn}
            whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
            transition={{ duration: 0.2 }}
        >
            <span className="counter-number block text-5xl font-bold text-blue-600 mb-2">
                {count.toLocaleString()}{suffix}
            </span>
            <span className="text-gray-600 font-medium">{label}</span>
        </motion.div>
    );
};

const HomePage = ({ onNewsletterClick }) => {
    // Navigation hook
    const navigate = useNavigate();
    
    // Carousel state and data
    const [currentSlide, setCurrentSlide] = useState(0);
    const intervalRef = useRef(null);

    // Refs for various sections
    const interactiveStatsRef = useRef(null);
    const isInteractiveStatsVisible = useIntersectionObserver(interactiveStatsRef);

    const carouselData = [
        {
            image: "/images/IMG-20250605-WA0410.jpg",
            title: "Empowering Children Through Education",
            subtitle: "Ummeed Ki Kiran Foundation Trust - Building brighter futures for underprivileged children since 2016",
            buttonText: "Learn More"
        },
        {
            image: "/images/IMG-20250605-WA0408.jpg",
            title: "Quality Education for All",
            subtitle: "Providing proper education to every under-privileged child and making them literate",
            buttonText: "Discover More"
        },
        {
            image: "/images/IMG-20250605-WA0411.jpg",
            title: "Community Support & Hope",
            subtitle: "Working with slum children in Varanasi to create lasting change and opportunities",
            buttonText: "Join Us"
        }
    ];

    // Success stories data
    const successStories = [
        {
            title: "Priya's Education Journey",
            beforeImage: "/images/IMG-20250605-WA0383.jpg",
            afterImage: "/images/IMG-20250605-WA0390.jpg",
            beforeText: "Priya was working as a child laborer at age 8...",
            afterText: "Today, Priya is a college graduate and works as a teacher in her village!"
        },
        {
            title: "Ravi's Health Recovery",
            beforeImage: "/images/IMG-20250605-WA0394.jpg",
            afterImage: "/images/IMG-20250605-WA0402.jpg",
            beforeText: "Ravi suffered from malnutrition and poor health...",
            afterText: "Now healthy and strong, Ravi leads sports activities for other children!"
        },
        {
            title: "Meera's Empowerment",
            beforeImage: "/images/IMG-20250605-WA0389.jpg",
            afterImage: "/images/IMG-20250605-WA0391.jpg",
            beforeText: "Meera was trapped in poverty with no opportunities...",
            afterText: "Through vocational training, Meera now runs her own tailoring business!"
        }
    ];

    // Function to start/restart the auto-slide timer
    const startAutoSlide = useCallback(() => {
        // Clear existing interval if any
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Start new interval
        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % carouselData.length);
        }, 5000); // Change slide every 5 seconds
    }, [carouselData.length]);

    // Function to reset timer (used for manual navigation)
    const resetTimer = () => {
        startAutoSlide();
    };

    // Navigation functions for carousel buttons
    const handleCarouselButtonClick = () => {
        navigate('/about');
    };

    // Function to handle "Watch Our Story" button (commented out as no video exists)
    // const handleWatchStoryClick = () => {
    //     // Navigate to video or media page when available
    // };

    // Auto-slide functionality
    useEffect(() => {
        startAutoSlide();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [startAutoSlide]);

    // Navigation functions with timer reset
    const goToSlide = (index) => {
        setCurrentSlide(index);
        resetTimer(); // Reset timer when user manually navigates
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % carouselData.length);
        resetTimer(); // Reset timer when user clicks next
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + carouselData.length) % carouselData.length);
        resetTimer(); // Reset timer when user clicks previous
    };

    return (
        <div className="font-sans">
            {/* Floating Donation Widget */}
            <FloatingDonationWidget />

            {/* Live Updates Ticker */}
            <LiveUpdatesTicker />

            {/* Hero Section with Carousel */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-black bg-opacity-60 z-10"></div>

                {/* Carousel container */}
                <div className="relative h-[600px]">
                    <div
                        className="flex transition-transform duration-700 ease-in-out h-full"
                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                        {carouselData.map((slide, index) => (
                            <div key={index} className="min-w-full h-full relative">
                                <img
                                    src={slide.image}
                                    alt={slide.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ))}
                    </div>

                    {/* Carousel content overlay */}
                    <div className="absolute inset-0 flex items-center justify-center z-20">
                        <div className="mx-auto px-4 text-center text-white max-w-5xl">
                            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fadeInUp">
                                {carouselData[currentSlide].title}
                            </h1>
                            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto animate-fadeInUp animation-delay-200">
                                {carouselData[currentSlide].subtitle}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button 
                                    onClick={handleCarouselButtonClick}
                                    className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-8 rounded-full transition duration-300 transform hover:-translate-y-1 animate-fadeInUp animation-delay-400"
                                >
                                    {carouselData[currentSlide].buttonText}
                                </button>
                                {/* Commented out - no video story available */}
                                {/* <button className="bg-transparent border-2 border-white hover:bg-white hover:text-gray-800 text-white font-semibold py-3 px-8 rounded-full transition duration-300 transform hover:-translate-y-1 animate-fadeInUp animation-delay-500">
                                    Watch Our Story
                                </button> */}
                            </div>
                        </div>
                    </div>

                    {/* Carousel controls */}
                    <div className="absolute inset-y-0 left-0 flex items-center z-20">
                        <button
                            onClick={prevSlide}
                            className="bg-black bg-opacity-30 hover:bg-opacity-50 text-white p-3 rounded-r-lg transition-all duration-300 hover:scale-110"
                        >
                            <FaChevronLeft className="text-2xl" />
                        </button>
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center z-20">
                        <button
                            onClick={nextSlide}
                            className="bg-black bg-opacity-30 hover:bg-opacity-50 text-white p-3 rounded-l-lg transition-all duration-300 hover:scale-110"
                        >
                            <FaChevronRight className="text-2xl" />
                        </button>
                    </div>
                </div>

                {/* Carousel indicators */}
                <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2 z-20">
                    {carouselData.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`h-3 transition-all duration-300 rounded-full ${index === currentSlide
                                ? 'w-14 bg-white'
                                : 'w-3 bg-white bg-opacity-50 hover:bg-opacity-75'
                                }`}
                        />
                    ))}
                </div>
            </section>

            {/* About Section */}
            <motion.section
                className="py-20 bg-white overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div variants={fadeInLeft}>
                            <motion.h2
                                className="text-4xl md:text-5xl font-bold text-gray-800 mb-6"
                                variants={fadeInUp}
                                whileHover={{ scale: 1.02 }}
                                transition={{ duration: 0.3 }}
                            >
                                About Ummeed Ki Kiran Foundation Trust
                            </motion.h2>
                            <motion.div className="space-y-6 text-lg text-gray-700" variants={staggerContainer}>
                                <motion.p
                                    variants={fadeInUp}
                                    className="relative"
                                    whileHover={{ x: 10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <span className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 rounded-full transform scale-y-0 hover:scale-y-100 transition-transform duration-300 origin-top"></span>
                                    <span className="pl-6">
                                        Ummeed Ki Kiran Foundation Trust is a non-governmental organization founded in 2016 by service-minded and dedicated youth of Varanasi in the state of Uttar Pradesh.
                                    </span>
                                </motion.p>
                                <motion.p
                                    variants={fadeInUp}
                                    className="relative"
                                    whileHover={{ x: 10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <span className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-green-500 to-teal-500 rounded-full transform scale-y-0 hover:scale-y-100 transition-transform duration-300 origin-top"></span>
                                    <span className="pl-6">
                                        The team of UKKFT started the organisation with a view to make society a better place and work for the people who are in need. The NGO is registered under the Indian Trust Act and focuses on providing education to slum children.
                                    </span>
                                </motion.p>
                                <motion.p
                                    variants={fadeInUp}
                                    className="relative"
                                    whileHover={{ x: 10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <span className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-red-500 to-pink-500 rounded-full transform scale-y-0 hover:scale-y-100 transition-transform duration-300 origin-top"></span>
                                    <span className="pl-6">
                                        We believe in making children capable of differentiating between what is wrong and what is right, empowering them with knowledge and skills for a better future.
                                    </span>
                                </motion.p>

                                {/* Interactive Statistics Cards */}
                                <motion.div
                                    className="grid grid-cols-2 gap-4 mt-8"
                                    variants={staggerContainer}
                                >
                                    <motion.div
                                        className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 cursor-pointer group"
                                        variants={scaleIn}
                                        whileHover={{ scale: 1.05, y: -5 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <div className="text-center">
                                            <FaUsers className="text-3xl text-blue-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                            <div className="text-2xl font-bold text-blue-800">8+</div>
                                            <div className="text-sm text-blue-700">Years of Service</div>
                                        </div>
                                    </motion.div>
                                    <motion.div
                                        className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-200 cursor-pointer group"
                                        variants={scaleIn}
                                        whileHover={{ scale: 1.05, y: -5 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <div className="text-center">
                                            <FaMapMarkerAlt className="text-3xl text-green-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                            <div className="text-2xl font-bold text-green-800">25+</div>
                                            <div className="text-sm text-green-700">Villages Reached</div>
                                        </div>
                                    </motion.div>
                                </motion.div>

                                {/* Interactive Badges */}
                                <motion.div
                                    className="flex flex-wrap gap-4 mt-8"
                                    variants={staggerContainer}
                                >
                                    <motion.div
                                        className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold cursor-pointer relative overflow-hidden group"
                                        variants={scaleIn}
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="absolute inset-0 bg-blue-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></span>
                                        <span className="relative group-hover:text-white transition-colors">Founded 2016</span>
                                    </motion.div>
                                    <motion.div
                                        className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold cursor-pointer relative overflow-hidden group"
                                        variants={scaleIn}
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="absolute inset-0 bg-green-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></span>
                                        <span className="relative group-hover:text-white transition-colors">Varanasi Based</span>
                                    </motion.div>
                                    <motion.div
                                        className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold cursor-pointer relative overflow-hidden group"
                                        variants={scaleIn}
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="absolute inset-0 bg-purple-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></span>
                                        <span className="relative group-hover:text-white transition-colors">Education Focus</span>
                                    </motion.div>
                                </motion.div>

                                {/* Call to Action Button */}
                                <motion.div
                                    className="mt-8"
                                    variants={fadeInUp}
                                >
                                    <motion.button
                                        onClick={() => navigate('/about')}
                                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg relative overflow-hidden group"
                                        whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                                        <span className="relative flex items-center gap-2">
                                            Learn More About Us
                                            <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </motion.button>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                        <motion.div variants={fadeInRight}>
                            <div className="relative group">
                                <motion.img
                                    src="/images/IMG-20250605-WA0396.jpg"
                                    alt="About Ummeed Ki Kiran Foundation"
                                    className="w-full h-[500px] object-cover rounded-2xl shadow-lg transition-transform duration-500 group-hover:scale-105"
                                    whileHover={{ scale: 1.02 }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-2xl group-hover:from-black/60 transition-all duration-300"></div>

                                {/* Floating Info Cards */}
                                <motion.div
                                    className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg"
                                    initial={{ opacity: 0, y: -20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <div className="flex items-center gap-2">
                                        <FaAward className="text-2xl text-yellow-600" />
                                        <div>
                                            <div className="font-bold text-gray-800">Trusted NGO</div>
                                            <div className="text-sm text-gray-600">Registered Trust</div>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div
                                    className="absolute bottom-6 left-6 right-6 text-white"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.7 }}
                                >
                                    <h3 className="text-2xl font-bold mb-2 group-hover:text-yellow-300 transition-colors">Transforming Lives Since 2016</h3>
                                    <p className="text-sm opacity-90 group-hover:opacity-100 transition-opacity">Dedicated to providing quality education and empowerment to underprivileged children</p>

                                    {/* Progress Indicators */}
                                    <div className="mt-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Education Impact</span>
                                            <span>85%</span>
                                        </div>
                                        <div className="w-full bg-white/20 rounded-full h-2">
                                            <motion.div
                                                className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full"
                                                initial={{ width: 0 }}
                                                whileInView={{ width: "85%" }}
                                                transition={{ duration: 1.5, delay: 1 }}
                                            ></motion.div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Decorative Elements */}
                                <motion.div
                                    className="absolute -top-4 -left-4 w-8 h-8 bg-blue-500 rounded-full opacity-70"
                                    animate={{
                                        scale: [1, 1.2, 1],
                                        rotate: [0, 180, 360]
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />
                                <motion.div
                                    className="absolute -bottom-4 -right-4 w-6 h-6 bg-purple-500 rounded-full opacity-70"
                                    animate={{
                                        scale: [1, 0.8, 1],
                                        rotate: [360, 180, 0]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Our Goal Section */}
            <motion.section
                className="py-20 bg-gradient-to-r from-green-50 to-blue-50 relative overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                {/* Background Decorative Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <motion.div
                        className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full opacity-20"
                        animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                        className="absolute bottom-20 right-10 w-24 h-24 bg-blue-200 rounded-full opacity-20"
                        animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <motion.div className="text-center mb-16" variants={fadeInUp}>
                        <motion.h2
                            className="text-4xl md:text-5xl font-bold text-gray-800 mb-6"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.3 }}
                        >
                            Our Goal
                        </motion.h2>
                        <motion.p
                            className="text-xl text-gray-600 max-w-3xl mx-auto"
                            whileHover={{ y: -5 }}
                            transition={{ duration: 0.3 }}
                        >
                            Working towards a society where every child has access to quality education and opportunities
                        </motion.p>
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div variants={fadeInLeft}>
                            <motion.div
                                className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden group cursor-pointer"
                                whileHover={{
                                    y: -10,
                                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                                    scale: 1.02
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                <motion.div
                                    className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-blue-500"
                                    initial={{ scaleX: 0 }}
                                    whileInView={{ scaleX: 1 }}
                                    transition={{ duration: 1, delay: 0.5 }}
                                />

                                <motion.div
                                    whileHover={{ rotate: 360, scale: 1.2 }}
                                    transition={{ duration: 0.5 }}
                                    className="inline-block"
                                >
                                    <FaBullseye className="text-4xl text-green-600 mb-6" />
                                </motion.div>

                                <motion.h3
                                    className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-green-600 transition-colors"
                                    whileHover={{ x: 10 }}
                                >
                                    Primary Goal
                                </motion.h3>

                                <motion.p
                                    className="text-lg text-gray-700 leading-relaxed mb-6"
                                    whileHover={{ x: 5 }}
                                >
                                    The NGO aims at providing education to slum children and making them capable of differentiating between what is wrong and what is right.
                                </motion.p>

                                <div className="space-y-4">
                                    {[
                                        { text: "Quality Education Access", delay: 0.1 },
                                        { text: "Decision-Making Capacity", delay: 0.2 },
                                        { text: "Moral Development", delay: 0.3 }
                                    ].map((item, index) => (
                                        <motion.div
                                            key={index}
                                            className="flex items-center gap-3 group/item cursor-pointer p-2 rounded-lg hover:bg-green-50 transition-colors"
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ delay: item.delay }}
                                            whileHover={{ x: 10, scale: 1.05 }}
                                        >
                                            <motion.div
                                                whileHover={{ scale: 1.3, rotate: 360 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <FaCheckCircle className="text-green-600 group-hover/item:text-green-700" />
                                            </motion.div>
                                            <span className="text-gray-700 group-hover/item:text-gray-800 group-hover/item:font-medium transition-all">
                                                {item.text}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Interactive Progress Ring */}
                                <motion.div
                                    className="mt-8 flex justify-center"
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ delay: 0.7 }}
                                >
                                    <div className="relative w-24 h-24">
                                        <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                                            <circle
                                                cx="50"
                                                cy="50"
                                                r="40"
                                                stroke="currentColor"
                                                strokeWidth="8"
                                                fill="transparent"
                                                className="text-gray-200"
                                            />
                                            <motion.circle
                                                cx="50"
                                                cy="50"
                                                r="40"
                                                stroke="currentColor"
                                                strokeWidth="8"
                                                fill="transparent"
                                                strokeLinecap="round"
                                                className="text-green-500"
                                                initial={{ pathLength: 0 }}
                                                whileInView={{ pathLength: 0.75 }}
                                                transition={{ duration: 2, delay: 1 }}
                                                style={{
                                                    strokeDasharray: "251.2",
                                                    strokeDashoffset: "62.8"
                                                }}
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-xl font-bold text-green-600">75%</span>
                                        </div>
                                    </div>
                                </motion.div>
                                <p className="text-center text-sm text-gray-600 mt-2">Goal Achievement</p>

                                {/* Hover Effect Overlay */}
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    initial={{ scale: 0 }}
                                    whileHover={{ scale: 1 }}
                                />
                            </motion.div>
                        </motion.div>

                        <motion.div variants={fadeInRight}>
                            <div className="relative group">
                                <motion.img
                                    src="/images/IMG-20250605-WA0410.jpg"
                                    alt="Children Education Goal"
                                    className="w-full h-[400px] object-cover rounded-2xl shadow-lg transition-all duration-500 group-hover:shadow-2xl relative z-10"
                                    whileHover={{ scale: 1.08, rotateY: 5, rotateX: 2 }}
                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                    style={{ transformStyle: "preserve-3d" }}
                                />

                                {/* Floating Achievement Badges */}
                                <motion.div
                                    className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg z-20"
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 }}
                                    whileHover={{ scale: 1.1, y: -5 }}
                                >
                                    <div className="flex items-center gap-2">
                                        <FaGraduationCap className="text-blue-600" />
                                        <span className="text-sm font-semibold text-gray-800">2000+ Students</span>
                                    </div>
                                </motion.div>

                                <motion.div
                                    className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg z-20"
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 }}
                                    whileHover={{ scale: 1.1, y: -5 }}
                                >
                                    <div className="flex items-center gap-2">
                                        <FaHeart className="text-red-600" />
                                        <span className="text-sm font-semibold text-gray-800">Lives Changed</span>
                                    </div>
                                </motion.div>

                                {/* Bottom Impact Card */}
                                <motion.div
                                    className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg z-20"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.9 }}
                                    whileHover={{ scale: 1.02 }}
                                >
                                    <h4 className="font-bold text-gray-800 mb-2">Education Impact</h4>
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div>
                                            <div className="text-lg font-bold text-blue-600">85%</div>
                                            <div className="text-xs text-gray-600">Literacy Rate</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-green-600">92%</div>
                                            <div className="text-xs text-gray-600">Retention</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-purple-600">78%</div>
                                            <div className="text-xs text-gray-600">Success Rate</div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Subtle Hover Glow */}
                                <motion.div
                                    className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-blue-100/30 to-transparent opacity-0 pointer-events-none z-5"
                                    whileHover={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Our Objectives Section */}
            <motion.section
                className="py-20 bg-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={fadeInUp}>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Our Objectives</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our comprehensive approach to community development and child empowerment
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        variants={staggerContainer}
                    >
                        {[
                            {
                                icon: <FaGraduationCap className="text-3xl" />,
                                title: "Increase Literacy",
                                description: "Creating awareness amongst people about the importance of education and providing quality learning opportunities.",
                                color: "blue"
                            },
                            {
                                icon: <FaHandHoldingHeart className="text-3xl" />,
                                title: "Reduce Poverty",
                                description: "Improving economic status of individuals through income generation activities and vocational training.",
                                color: "green"
                            },
                            {
                                icon: <FaHeartbeat className="text-3xl" />,
                                title: "Improve Health Status",
                                description: "Health education, environmental cleanliness, and increasing access to primary health care centers.",
                                color: "red"
                            },
                            {
                                icon: <FaUsers className="text-3xl" />,
                                title: "Women Empowerment",
                                description: "Organizing women into self-help groups and developing their leadership qualities for decision making.",
                                color: "purple"
                            },
                            {
                                icon: <FaShieldAlt className="text-3xl" />,
                                title: "Eliminate Child Labor",
                                description: "Working to eliminate bonded, forced, child labor and slavery while promoting gender equality.",
                                color: "orange"
                            },
                            {
                                icon: <FaChild className="text-3xl" />,
                                title: "Drug Awareness",
                                description: "Making children aware of the ill-effects of drugs and promoting healthy lifestyle choices.",
                                color: "indigo"
                            }
                        ].map((objective, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                                variants={scaleIn}
                                whileHover={{ y: -10, scale: 1.02 }}
                            >
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${objective.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                                    objective.color === 'green' ? 'bg-green-100 text-green-600' :
                                        objective.color === 'red' ? 'bg-red-100 text-red-600' :
                                            objective.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                                                objective.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                                                    'bg-indigo-100 text-indigo-600'
                                    }`}>
                                    {objective.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors">
                                    {objective.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {objective.description}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Major Activities Section */}
            <motion.section
                className="py-20 bg-gradient-to-br from-gray-50 to-blue-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={fadeInUp}>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Major Activities</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our core programs that create lasting impact in the communities we serve
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 lg:grid-cols-2 gap-12"
                        variants={staggerContainer}
                    >
                        {[
                            {
                                icon: <FaGraduationCap className="text-4xl text-white" />,
                                title: "Health Care Activities",
                                description: "Comprehensive healthcare programs including medical camps, health awareness sessions, vaccination drives, and providing access to basic medical facilities in rural and slum areas.",
                                image: "/images/IMG-20250605-WA0405.jpg",
                                features: ["Free Medical Camps", "Health Awareness", "Vaccination Drives", "Primary Healthcare"],
                                color: "from-red-500 to-pink-500"
                            },
                            {
                                icon: <FaHandsHelping className="text-4xl text-white" />,
                                title: "Vocational Training",
                                description: "Skill development programs for children and unemployed women, focusing on practical skills that lead to sustainable employment and income generation opportunities.",
                                image: "/images/IMG-20250605-WA0417.jpg",
                                features: ["Skill Development", "Employment Training", "Income Generation", "Women Empowerment"],
                                color: "from-green-500 to-teal-500"
                            },
                            {
                                icon: <FaChild className="text-4xl text-white" />,
                                title: "Children Development",
                                description: "Holistic child development programs including education, personality development, sports activities, and creating a safe environment for children to grow and learn.",
                                image: "/images/IMG-20250605-WA0404.jpg",
                                features: ["Quality Education", "Personality Development", "Sports Activities", "Safe Environment"],
                                color: "from-blue-500 to-purple-500"
                            },
                            {
                                icon: <FaShieldAlt className="text-4xl text-white" />,
                                title: "Self-Defence Training",
                                description: "Empowering women and children with self-defense skills, building confidence, and promoting safety awareness in communities through specialized training programs.",
                                image: "/images/IMG-20250605-WA0389.jpg",
                                features: ["Self-Defense Skills", "Confidence Building", "Safety Awareness", "Community Protection"],
                                color: "from-orange-500 to-red-500"
                            }
                        ].map((activity, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-2xl shadow-lg overflow-hidden group"
                                variants={scaleIn}
                                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="relative overflow-hidden h-64">
                                    <img
                                        src={activity.image}
                                        alt={activity.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className={`absolute inset-0 bg-gradient-to-r ${activity.color} opacity-80`}></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <div className="mb-4">
                                                {activity.icon}
                                            </div>
                                            <h3 className="text-2xl font-bold">{activity.title}</h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-8">
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {activity.description}
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {activity.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center gap-2">
                                                <FaCheckCircle className="text-green-500 text-sm" />
                                                <span className="text-sm text-gray-700">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Interactive Statistics Section */}
            <motion.section
                className="py-16 bg-gradient-to-r from-blue-50 to-purple-50"
                ref={interactiveStatsRef}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-center mb-12"
                        variants={fadeInUp}
                    >
                        Our Growing Impact
                    </motion.h2>
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                    >
                        <InteractiveStats
                            targetValue={2000}
                            suffix="+"
                            label="Children Educated"
                            icon={<FaGraduationCap />}
                            color="from-blue-400 to-blue-600"
                            shouldStart={isInteractiveStatsVisible}
                        />
                        <InteractiveStats
                            targetValue={500}
                            suffix="+"
                            label="Families Supported"
                            icon={<FaUsers />}
                            color="from-green-400 to-green-600"
                            shouldStart={isInteractiveStatsVisible}
                        />
                        <InteractiveStats
                            targetValue={25}
                            suffix="+"
                            label="Villages Reached"
                            icon={<FaMapMarkerAlt />}
                            color="from-purple-400 to-purple-600"
                            shouldStart={isInteractiveStatsVisible}
                        />
                        <InteractiveStats
                            targetValue={8}
                            suffix=""
                            label="Years of Service"
                            icon={<FaAward />}
                            color="from-red-400 to-red-600"
                            shouldStart={isInteractiveStatsVisible}
                        />
                    </motion.div>
                </div>
            </motion.section>

            {/* Success Stories Section */}
            <motion.section
                className="py-16"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-12" variants={fadeInUp}>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Stories of Transformation</h2>
                        <p className="text-xl text-gray-600">Hover over the cards to see the incredible journey of change</p>
                    </motion.div>
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-8"
                        variants={staggerContainer}
                    >
                        {successStories.map((story, index) => (
                            <SuccessStoryCard key={index} story={story} />
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Testimonials Section */}
            <motion.section
                className="py-16 bg-gray-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div variants={fadeInUp}>
                        <TestimonialCarousel />
                    </motion.div>
                </div>
            </motion.section>

            {/* Volunteer Opportunities Section */}
            <motion.section
                className="py-16"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-12" variants={fadeInUp}>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
                        <p className="text-xl text-gray-600">Volunteer opportunities to make a real difference</p>
                    </motion.div>
                    <VolunteerOpportunities />
                </div>
            </motion.section>

            {/* Interactive Donation Section */}
            <motion.section
                className="py-20 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-6xl mx-auto px-4">
                    <motion.div className="text-center mb-12" variants={fadeInUp}>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Make a Difference Today</h2>
                        <p className="text-xl text-gray-600">Choose how you want to help transform lives</p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-8"
                        variants={staggerContainer}
                    >
                        {/* Monthly Donation */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-8 text-center relative overflow-hidden"
                            variants={scaleIn}
                            whileHover={{ y: -10, scale: 1.02 }}
                        >
                            <motion.div
                                className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-purple-500"
                                initial={{ width: 0 }}
                                whileInView={{ width: '100%' }}
                                transition={{ duration: 1, delay: 0.5 }}
                            />
                            <div className="text-4xl mb-4">💝</div>
                            <h3 className="text-2xl font-bold mb-4 text-blue-600">Monthly Giving</h3>
                            <p className="text-gray-600 mb-6">Sustain our programs with regular support</p>
                            <div className="mb-6">
                                <span className="text-3xl font-bold text-blue-600">₹500</span>
                                <span className="text-gray-500">/month</span>
                            </div>
                            <ul className="text-left space-y-2 mb-6 text-sm text-gray-600">
                                <li className="flex items-center gap-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span>Education for 2 children</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span>Monthly health checkups</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span>Regular updates & photos</span>
                                </li>
                            </ul>
                            <motion.button
                                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Start Monthly Giving
                            </motion.button>
                        </motion.div>

                        {/* One-time Donation */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-8 text-center relative overflow-hidden border-2 border-orange-200"
                            variants={scaleIn}
                            whileHover={{ y: -10, scale: 1.02 }}
                        >
                            <motion.div
                                className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 to-red-500"
                                initial={{ width: 0 }}
                                whileInView={{ width: '100%' }}
                                transition={{ duration: 1, delay: 0.7 }}
                            />
                            <div className="absolute -top-3 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                                POPULAR
                            </div>
                            <div className="text-4xl mb-4">🎯</div>
                            <h3 className="text-2xl font-bold mb-4 text-orange-600">One-Time Impact</h3>
                            <p className="text-gray-600 mb-6">Make an immediate difference</p>
                            <div className="grid grid-cols-2 gap-2 mb-6">
                                {[1000, 2500, 5000, 10000].map((amount) => (
                                    <motion.button
                                        key={amount}
                                        className="border-2 border-orange-200 hover:border-orange-500 hover:bg-orange-50 py-2 rounded-lg text-sm transition-colors"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        ₹{amount.toLocaleString()}
                                    </motion.button>
                                ))}
                            </div>
                            <motion.button
                                className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Donate Now
                            </motion.button>
                        </motion.div>

                        {/* Sponsor a Child */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-8 text-center relative overflow-hidden"
                            variants={scaleIn}
                            whileHover={{ y: -10, scale: 1.02 }}
                        >
                            <motion.div
                                className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-blue-500"
                                initial={{ width: 0 }}
                                whileInView={{ width: '100%' }}
                                transition={{ duration: 1, delay: 0.9 }}
                            />
                            <div className="text-4xl mb-4">👶</div>
                            <h3 className="text-2xl font-bold mb-4 text-green-600">Sponsor a Child</h3>
                            <p className="text-gray-600 mb-6">Build a personal connection</p>
                            <div className="mb-6">
                                <span className="text-3xl font-bold text-green-600">₹1500</span>
                                <span className="text-gray-500">/month</span>
                            </div>
                            <ul className="text-left space-y-2 mb-6 text-sm text-gray-600">
                                <li className="flex items-center gap-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span>Full education & healthcare</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span>Personal child profile</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span>Letters & progress reports</span>
                                </li>
                            </ul>
                            <motion.button
                                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Sponsor a Child
                            </motion.button>
                        </motion.div>
                    </motion.div>

                    {/* Trust Indicators */}
                    <motion.div
                        className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-center"
                        variants={staggerContainer}
                    >
                        <motion.div className="flex items-center justify-center gap-2" variants={fadeInUp}>
                            <FaCheckCircle className="text-green-500" />
                            <span className="text-sm text-gray-600">Tax Deductible</span>
                        </motion.div>
                        <motion.div className="flex items-center justify-center gap-2" variants={fadeInUp}>
                            <FaCheckCircle className="text-green-500" />
                            <span className="text-sm text-gray-600">Secure Payments</span>
                        </motion.div>
                        <motion.div className="flex items-center justify-center gap-2" variants={fadeInUp}>
                            <FaCheckCircle className="text-green-500" />
                            <span className="text-sm text-gray-600">Transparent Use</span>
                        </motion.div>
                        <motion.div className="flex items-center justify-center gap-2" variants={fadeInUp}>
                            <FaCheckCircle className="text-green-500" />
                            <span className="text-sm text-gray-600">Regular Updates</span>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Quick Impact Showcase */}
            <motion.section
                className="py-16 bg-gradient-to-r from-indigo-100 via-purple-50 to-pink-100"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-12" variants={fadeInUp}>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Latest Updates & Achievements</h2>
                        <p className="text-xl text-gray-600">See what your support has helped us accomplish</p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={staggerContainer}
                    >
                        {[
                            {
                                icon: "🎓",
                                title: "New School Opened",
                                description: "Opening a new learning center in Kachhawa village",
                                date: "Jan 2024",
                                color: "from-blue-400 to-blue-600"
                            },
                            {
                                icon: "💉",
                                title: "Health Camp Success",
                                description: "200+ children received vaccinations and health checkups",
                                date: "Dec 2023",
                                color: "from-green-400 to-green-600"
                            },
                            {
                                icon: "👩‍💼",
                                title: "Women Empowered",
                                description: "50 women completed vocational training program",
                                date: "Nov 2023",
                                color: "from-purple-400 to-purple-600"
                            }
                        ].map((update, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-xl shadow-lg p-6 relative overflow-hidden"
                                variants={scaleIn}
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                <motion.div
                                    className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${update.color}`}
                                    initial={{ width: 0 }}
                                    whileInView={{ width: '100%' }}
                                    transition={{ duration: 1, delay: index * 0.2 }}
                                />
                                <div className="flex items-start gap-4">
                                    <div className="text-3xl">{update.icon}</div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold mb-2">{update.title}</h3>
                                        <p className="text-gray-600 text-sm mb-3">{update.description}</p>
                                        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{update.date}</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        className="mt-12 text-center"
                        variants={fadeInUp}
                    >
                        <motion.button
                            onClick={() => navigate('/media')}
                            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg"
                            whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                            whileTap={{ scale: 0.95 }}
                        >
                            View All Our Updates →
                        </motion.button>
                    </motion.div>
                </div>
            </motion.section>

            {/* Newsletter Subscription Section */}
            <motion.section
                className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <motion.div className="mb-8" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold mb-4" variants={fadeInUp}>
                            Stay Connected With Our Mission
                        </motion.h2>
                        <motion.p className="text-xl md:text-2xl mb-2 opacity-90" variants={fadeInUp}>
                            Subscribe to our newsletter and be the first to know about our impact stories, new projects, and ways you can help transform lives.
                        </motion.p>
                        <motion.p className="text-lg opacity-80" variants={fadeInUp}>
                            Join our community of changemakers today!
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto mb-8"
                        variants={scaleIn}
                    >
                        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-8 w-full">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                                <div>
                                    <div className="text-3xl font-bold mb-2">📧</div>
                                    <p className="text-sm opacity-90">Monthly Updates</p>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold mb-2">📖</div>
                                    <p className="text-sm opacity-90">Impact Stories</p>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold mb-2">🎯</div>
                                    <p className="text-sm opacity-90">Project Updates</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.button
                        onClick={onNewsletterClick}
                        className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-full text-lg shadow-lg"
                        variants={fadeInUp}
                        whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        📰 Subscribe to Newsletter
                    </motion.button>

                    <motion.p className="text-sm opacity-75 mt-4" variants={fadeInUp}>
                        ✅ No spam, just meaningful updates • ✅ Unsubscribe anytime • ✅ Your privacy is protected
                    </motion.p>
                </div>
            </motion.section>

            {/* Contact Information Section */}
            <motion.section
                className="py-16 bg-gray-100"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-center mb-12"
                        variants={fadeInUp}
                    >
                        Get In Touch
                    </motion.h2>
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-8"
                        variants={staggerContainer}
                    >
                        <motion.div
                            className="text-center bg-white p-8 rounded-lg shadow-md"
                            variants={scaleIn}
                            whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                            transition={{ duration: 0.3 }}
                        >
                            <FaPhone className="text-3xl text-blue-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                            <p className="text-gray-600">+917985947303</p>
                        </motion.div>
                        <motion.div
                            className="text-center bg-white p-8 rounded-lg shadow-md"
                            variants={scaleIn}
                            whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                            transition={{ duration: 0.3 }}
                        >
                            <FaEnvelope className="text-3xl text-blue-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                            <p className="text-gray-600">support@ummeedkikiran.org</p>
                        </motion.div>
                        <motion.div
                            className="text-center bg-white p-8 rounded-lg shadow-md"
                            variants={scaleIn}
                            whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                            transition={{ duration: 0.3 }}
                        >
                            <FaMapMarkerAlt className="text-3xl text-blue-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Visit Us</h3>
                            <p className="text-gray-600">Varanasi, Uttar Pradesh</p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Social Media Section */}
            <motion.section
                className="py-10 border-t border-gray-200"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.5 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="flex flex-col md:flex-row justify-between items-center"
                        variants={staggerContainer}
                    >
                        <motion.p className="text-gray-600 mb-4 md:mb-0" variants={fadeInLeft}>
                            Follow us on
                        </motion.p>
                        <motion.div className="flex gap-4" variants={fadeInRight}>
                            {/* Commented out - no Facebook page available */}
                            {/* <motion.a
                                href="#"
                                className="text-blue-800 hover:text-blue-600 text-xl"
                                whileHover={{ scale: 1.2, rotate: 5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ duration: 0.2 }}
                            >
                                <FaFacebookF />
                            </motion.a> */}
                            <motion.a
                                href="https://www.instagram.com/ummeedkikiranfoundation?igsh=bjRqanpzenVsNjht"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-pink-600 hover:text-pink-500 text-xl"
                                whileHover={{ scale: 1.2, rotate: -5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ duration: 0.2 }}
                            >
                                <FaInstagram />
                            </motion.a>
                            {/* Commented out - no Twitter page available */}
                            {/* <motion.a
                                href="#"
                                className="text-blue-400 hover:text-blue-500 text-xl"
                                whileHover={{ scale: 1.2, rotate: 5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ duration: 0.2 }}
                            >
                                <FaTwitter />
                            </motion.a> */}
                            {/* Commented out - no LinkedIn page available */}
                            {/* <motion.a
                                href="#"
                                className="text-blue-700 hover:text-blue-800 text-xl"
                                whileHover={{ scale: 1.2, rotate: -5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ duration: 0.2 }}
                            >
                                <FaLinkedinIn />
                            </motion.a> */}
                            {/* Commented out - no YouTube page available */}
                            {/* <motion.a
                                href="#"
                                className="text-red-600 hover:text-red-700 text-xl"
                                whileHover={{ scale: 1.2, rotate: 5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ duration: 0.2 }}
                            >
                                <FaYoutube />
                            </motion.a> */}
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default HomePage;